import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { payrollApi } from "../../services/payrollApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

const STATUS_ORDER = { PENDING_APPROVAL: 0, DRAFT: 0, APPROVED: 1, SUPERSEDED: 2, REJECTED: 3 };

export default function WageHistoryTimelinePage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canViewPayroll } = usePermissions();
  const [history, setHistory] = useState([]);
  const [employeeName, setEmployeeName] = useState("");

  const COMP_LABEL = { HOURLY: t("mgmtPayroll.hourlyWage"), FIXED_ASSIGNMENT: t("mgmtPayroll.supplyPickupAssignmentPay") };
  const UNIT_LABEL = { HOURLY: t("mgmtPayroll.perHour"), FIXED_ASSIGNMENT: t("mgmtPayroll.perApprovedAssignment") };
  const compType = (p) => (p.compensation_type === "FIXED_ASSIGNMENT" ? "FIXED_ASSIGNMENT" : "HOURLY");
  const formatAmount = (p) => `¥${Number(p.hourly_rate).toLocaleString()} ${UNIT_LABEL[compType(p)].toLowerCase()}`;

  useEffect(() => {
    if (!canViewPayroll) return;
    payrollApi.wageHistory(employeeId).then((r) => setHistory(r.data)).catch(() => {});
    payrollApi.listEmployeesForPayroll().then((r) => {
      const e = r.data.find((emp) => emp.id === employeeId);
      if (e) setEmployeeName(e.legal_name);
    }).catch(() => {});
  }, [employeeId, canViewPayroll]);

  if (!canViewPayroll) return <p className="text-sm text-gray-500" data-testid="wage-history-access-denied">{t("mgmtPayroll.wageHistoryAccessDenied")}</p>;

  const sorted = [...history].sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1));
  const current = sorted.find((p) => p.status === "APPROVED" && !p.effective_to);
  const future = sorted.filter((p) => p.status === "APPROVED" && p.effective_from > new Date().toISOString().slice(0, 10));
  const drafts = sorted.filter((p) => p.status === "DRAFT" || p.status === "PENDING_APPROVAL");

  // Gap/overlap detection across APPROVED profiles ordered oldest->newest.
  const approvedChrono = sorted.filter((p) => p.status === "APPROVED" || p.status === "SUPERSEDED").slice().reverse();
  const warnings = [];
  for (let i = 1; i < approvedChrono.length; i++) {
    const prev = approvedChrono[i - 1];
    const curr = approvedChrono[i];
    if (prev.effective_to && prev.effective_to < curr.effective_from) {
      warnings.push(`${t("mgmtPayroll.gapBetween")} ${prev.effective_to} ${t("mgmtScheduling.to")} ${curr.effective_from} - ${t("mgmtPayroll.noActiveWageProfile")}`);
    }
  }

  return (
    <div className="max-w-4xl space-y-5" data-testid="wage-history-page">
      <PageHelpBanner pageKey="wage-history" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtPayroll.wageHistoryHelp")}</p>
      </PageHelpBanner>
      <button data-testid="back-to-employee-btn" onClick={() => navigate(`/management/employees/${employeeId}`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={15} /> {t("mgmtPayroll.backToEmployeeProfile")}
      </button>

      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtPayroll.wageHistory")}{employeeName ? ` - ${employeeName}` : ""}</h2>
        <p className="text-sm text-gray-500">{t("mgmtPayroll.wageHistorySubtitle")}</p>
      </div>

      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1" data-testid="wage-history-warnings">
          {warnings.map((w, i) => <p key={i} className="text-xs text-amber-800">{w}</p>)}
        </div>
      )}

      {current && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="current-wage-profile-card">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-semibold">{t("mgmtPayroll.current")} · {COMP_LABEL[compType(current)]}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatAmount(current)}</p>
          <p className="text-xs text-gray-600 mt-1">{t("mgmtPayroll.effectiveSince")} {current.effective_from} · {t("mgmtPayroll.transportation")}: ¥{Number(current.transportation_allowance).toLocaleString()}</p>
        </div>
      )}

      {future.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("mgmtPayroll.futureApprovedChanges")}</p>
          {future.map((p) => (
            <div key={p.id} className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm" data-testid={`future-wage-row-${p.id}`}>
              {COMP_LABEL[compType(p)]}: {formatAmount(p)} {t("mgmtPayroll.effective")} {p.effective_from}
            </div>
          ))}
        </div>
      )}

      {drafts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("mgmtPayroll.draftPendingChanges")}</p>
          {drafts.map((p) => (
            <div key={p.id} className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm flex items-center justify-between" data-testid={`draft-wage-row-${p.id}`}>
              <span>{COMP_LABEL[compType(p)]}: {formatAmount(p)} {t("mgmtPayroll.proposedFor")} {p.effective_from}</span>
              <StatusBadge status={p.status} />
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <p className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-gray-500 border-b border-gray-100">{t("mgmtPayroll.fullTimeline")}</p>
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.effectiveFrom")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.effectiveTo")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.compensationType")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.amount")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.unit")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.transportation")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.approvedBy")}</th></tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`wage-history-row-${p.id}`}>
                <td className="px-4 py-2 text-gray-800 font-semibold">{p.effective_from}</td>
                <td className="px-4 py-2 text-gray-600">{p.effective_to || t("mgmtPayroll.ongoing")}</td>
                <td className="px-4 py-2 text-gray-600">{COMP_LABEL[compType(p)]}</td>
                <td className="px-4 py-2 text-gray-600">¥{Number(p.hourly_rate).toLocaleString()}</td>
                <td className="px-4 py-2 text-gray-600">{UNIT_LABEL[compType(p)]}</td>
                <td className="px-4 py-2 text-gray-600">¥{Number(p.transportation_allowance).toLocaleString()}</td>
                <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-2 text-gray-500 text-xs">{p.approved_by || "-"}</td>
              </tr>
            ))}
            {sorted.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500" data-testid="no-wage-history-row">{t("mgmtPayroll.noWageProfiles")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
